import { useQueryClient } from '@tanstack/react-query'
import pluralize from 'pluralize'
import { useState } from 'react'

import { Dropzone } from '@/components/ui/dropzone'
import { FieldError } from '@/components/ui/field'
import { PARSER_ACCEPTED_FILE_TYPES } from '@/lib/fileUpload'
import { cn } from '@/lib/utils'
import {
  fillApplicationErrorMessage,
  type IntakeUploadResult,
  invalidateFillApplication,
  uploadIntake,
} from '@/models/fillApplication'
import { FillApplicationEvent, trackFillApplication } from './fillApplicationAnalytics'
import { intakeFilesProblem, MAX_INTAKE_FILE_MB } from './intakeLimits'

interface IntakeDropzoneProps {
  orgId: string
  size: 'large' | 'compact'
  disabled?: boolean
  onUploaded: (result: IntakeUploadResult) => void
}

function fileKinds(files: File[]): string[] {
  return [...new Set(files.map((file) => file.name.split('.').pop()?.toLowerCase() ?? ''))]
}

export function IntakeDropzone({ orgId, size, disabled, onUploaded }: IntakeDropzoneProps) {
  const queryClient = useQueryClient()
  const [uploadingCount, setUploadingCount] = useState(0)
  const [problem, setProblem] = useState<string | null>(null)

  async function handleFiles(files: File[]) {
    if (files.length === 0) return
    const limitProblem = intakeFilesProblem(files)
    if (limitProblem) {
      setProblem(limitProblem)
      return
    }
    setProblem(null)
    setUploadingCount(files.length)
    try {
      const result = await uploadIntake(orgId, files)
      trackFillApplication(FillApplicationEvent.FILES_DROPPED, {
        count: files.length,
        kinds: fileKinds(files),
      })
      onUploaded(result)
      await invalidateFillApplication(queryClient, orgId)
    } catch (error) {
      // FILE_REJECTED and DOT_REQUIRED say what to do; a rate limit comes back as a 403 with its own message.
      setProblem(fillApplicationErrorMessage(error))
    } finally {
      setUploadingCount(0)
    }
  }

  const uploading = uploadingCount > 0

  return (
    <div className="flex flex-col gap-2">
      <Dropzone
        multiple
        accept={PARSER_ACCEPTED_FILE_TYPES}
        maxSizeMB={MAX_INTAKE_FILE_MB}
        showFileHints={false}
        onMultipleChange={(files) => void handleFiles(files)}
        isLoading={uploading}
        disabled={disabled || uploading}
        maxWidth={1280}
        className={cn(size === 'large' ? 'min-h-48' : 'p-4')}
      >
        {size === 'large' && (
          <p className="text-ink-500 text-center text-sm">
            Drop the ACORD application, loss runs, the current policy, driver and truck lists: anything they
            sent.
          </p>
        )}
      </Dropzone>
      {uploading && (
        <p role="status" className="text-ink-500 text-sm">
          Uploading {pluralize('file', uploadingCount, true)}…
        </p>
      )}
      <FieldError className="text-xs">{problem}</FieldError>
      <p className="text-muted-foreground text-xs">
        PDFs, photos, Word, Excel, CSV or text. Up to 10 files, 25 MB each, 50 MB in total. Long driver or
        truck lists read best through Import on the Equipment tab.
      </p>
    </div>
  )
}
